import { createStore, createEffect, createEvent, combine } from 'effector';
import i18n from 'i18n';
import { $notificationStore } from '../notificationStore';
import { AlertsApi } from '@shared/api/rest';
import { type IQueryParams } from '@shared/api/models';
import {
  AddDeclinedAuthRequest,
  AlertSubscriptionResponse,
  DeleteDeclinedAuthRequest,
  IAlertsUpdate,
  Recipient,
  UpdateDeclinedAuthRequest,
} from '@shared/api/rest/alerts';
import { $accountByIdStore } from '../accounts';
import { $cardStore } from '../card';

const addDeclinedAuthEffect = createEffect((params: IQueryParams<AddDeclinedAuthRequest>) =>
  AlertsApi.addDeclinedAuth(params.request, params?.signal),
);
const updateDeclinedAuthEffect = createEffect((params: IQueryParams<IAlertsUpdate<UpdateDeclinedAuthRequest>>) =>
  AlertsApi.updateDeclinedAuth(params.request, params?.signal),
);
const deleteDeclinedAuthEffect = createEffect((params: IQueryParams<DeleteDeclinedAuthRequest>) => AlertsApi.deleteSubscription(params.request));

const getRecipientEvent = createEvent<Recipient[]>();
const addRecipientEvent = createEvent<Recipient>();
const removeRecipientEvent = createEvent<string>();
const clearEvent = createEvent();

addDeclinedAuthEffect.done.watch((result) => {
  if (result.result!.data?.accountId !== null) {
    const request = result.result!.data?.accountId;

    $accountByIdStore.events.get(request);
  }
  if (result.result!.data?.cardId !== null) {
    const request = result.result!.data?.cardId;

    $cardStore.events.getCard(request);
  }
});

updateDeclinedAuthEffect.done.watch((result) => {
  if (result.result!.data?.accountId !== null) {
    const request = result.result!.data?.accountId;
    $accountByIdStore.events.get(request);
  }
  if (result.result!.data?.cardId !== null) {
    const request = result.result!.data?.cardId;

    $cardStore.events.getCard(request);
  }
});

deleteDeclinedAuthEffect.done.watch((result) => {
  if (result.params.request.accountId !== undefined) {
    const accountId = result.params.request.accountId;
    $accountByIdStore.events.get(accountId);
  }
  if (result.params.request.cardId !== undefined) {
    const cardId = result.params.request.cardId;
    $cardStore.events.getCard(cardId);
  }
});

const $dataStore = createStore<AlertSubscriptionResponse | null>(null)
  .on(addDeclinedAuthEffect.done, (_, data) => data.result.data! || null)
  .on(deleteDeclinedAuthEffect.done, () => null)
  .reset(clearEvent);

const $recipientStore = createStore<Recipient[]>([])
  .on(addRecipientEvent, (state, recipient) => [...state, recipient])
  .on(removeRecipientEvent, (state, email) => state.filter((recipient) => recipient.email !== email))
  .on(getRecipientEvent, (_, newRecipient) => newRecipient)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(addDeclinedAuthEffect.pending, (_, data) => data)
  .on(addDeclinedAuthEffect.fail, () => false)
  .on(addDeclinedAuthEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(addDeclinedAuthEffect, () => null)
  .on(addDeclinedAuthEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(addDeclinedAuthEffect.done, () => null)
  .reset(clearEvent);
$errorStore.watch((x) => $notificationStore.events.setError(x));

export const $declinedAuthStore = {
  events: {
    addDeclinedAuth: addDeclinedAuthEffect,
    updateDeclinedAuth: updateDeclinedAuthEffect,
    deleteDeclinedAuth: deleteDeclinedAuthEffect,
    get: getRecipientEvent,
    add: addRecipientEvent,
    remove: removeRecipientEvent,
    clear: clearEvent,
  },
  store: combine({
    data: $dataStore,
    recipient: $recipientStore,
    loading: $loadingStore,
    error: $errorStore,
  }),
};
